import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { msg } from "@lingui/core/macro";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { Settings } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { roleApi } from "@/lib/api/admin-api";
import { cn } from "@/lib/utils";
import { satisfiesPermission } from "@/lib/permissions";
import type { UserRoleAssignment, PermissionGroup } from "@/lib/api/admin-api";

// ── Label maps ───────────────────────────────────────────────────────────────

/**
 * Human copy for each permission the API enforces.
 *
 * Keys must exist in the API's `PERMISSION_CATALOGUE`
 * (`yeschef-api/src/common/permissions/permission-catalogue.ts`). This map had
 * drifted onto a vocabulary nothing gates on — `cost:read`, `company:write`,
 * `recipe:publish`, `user:write` — while every `:create`/`:update`/`:delete`
 * verb the API actually checks fell through to the raw action name. A missing
 * key is only cosmetic (see `getPermissionLabel`), so this list is allowed to
 * lag a new permission; a key naming a permission that does not exist is not,
 * because it advertises access nobody can hold.
 */
const PERMISSION_LABELS: Record<string, MessageDescriptor> = {
  "organization:read":      msg`View the organization`,
  "organization:create":    msg`Create organizations`,
  "organization:update":    msg`Edit organization settings`,
  "organization:delete":    msg`Delete organizations`,
  "company:read":           msg`View concepts`,
  "company:create":         msg`Create concepts`,
  "company:update":         msg`Edit concepts`,
  "company:delete":         msg`Delete concepts`,
  "region:read":            msg`View regions`,
  "region:create":          msg`Create regions`,
  "region:update":          msg`Edit regions`,
  "region:delete":          msg`Delete regions`,
  "location:read":          msg`View locations`,
  "location:create":        msg`Create locations`,
  "location:update":        msg`Edit locations`,
  "location:delete":        msg`Delete locations`,
  "recipe:read":            msg`View recipes`,
  "recipe:create":          msg`Create recipes`,
  "recipe:update":          msg`Edit existing recipes`,
  "recipe:delete":          msg`Delete recipes`,
  "material:read":          msg`View ingredients`,
  "material:create":        msg`Create ingredients`,
  "material:update":        msg`Edit ingredients`,
  "menu:read":              msg`View menus`,
  "menu:write":             msg`Manage menus`,
  "inventory:read":         msg`View inventory`,
  "inventory:write":        msg`Manage inventory`,
  "cooking-session:read":   msg`View cooking sessions`,
  "cooking-session:create": msg`Start cooking sessions`,
  "cooking-session:update": msg`Edit cooking sessions`,
  "cooking-session:delete": msg`Delete cooking sessions`,
  "cooking-session:assign": msg`Assign cooks to sessions`,
  "cooking-session:cook":   msg`Cook a session`,
  "order:read":             msg`View orders`,
  "order:create":           msg`Create orders`,
  "order:update":           msg`Edit orders`,
  "user:read":              msg`View users`,
  "user:create":            msg`Create users`,
  "user:update":            msg`Edit users`,
  "user:delete":            msg`Delete users`,
  "role:read":              msg`View roles`,
  "role:write":             msg`Manage roles`,
  "role:delete":            msg`Delete roles`,
  "role:assign":            msg`Assign roles to users`,
  "scope:write":            msg`Manage user access scopes`,
  "audit:read":             msg`View the activity log`,
};

const RESOURCE_LABELS: Record<string, { label: MessageDescriptor; description: MessageDescriptor }> = {
  organization:     { label: msg`Organization`,      description: msg`The tenant itself and its settings.` },
  recipe:           { label: msg`Recipes`,          description: msg`Create and edit recipes.` },
  user:             { label: msg`Users`,             description: msg`Manage user accounts and organisation membership.` },
  company:          { label: msg`Concepts`,          description: msg`Manage concepts and their settings.` },
  order:            { label: msg`Orders`,            description: msg`Create and view orders.` },
  location:         { label: msg`Locations`,         description: msg`Access and manage physical locations.` },
  region:           { label: msg`Regions`,           description: msg`Access and manage regions.` },
  role:             { label: msg`Roles & Permissions`, description: msg`Manage roles and permission assignments.` },
  inventory:        { label: msg`Inventory`,         description: msg`View and manage stock levels.` },
  menu:             { label: msg`Menus`,             description: msg`View and manage menus.` },
  "cooking-session":{ label: msg`Cooking Sessions`,  description: msg`Access cooking session data.` },
  material:         { label: msg`Ingredients`,       description: msg`View and manage ingredient/material catalogue.` },
  scope:            { label: msg`Access Scopes`,     description: msg`Decide which concepts, regions and locations a user can reach.` },
  audit:            { label: msg`Activity Log`,      description: msg`Read the record of who changed what.` },
};

function getPermissionLabel(resource: string, action: string, i18n: I18n): string {
  const code = `${resource}:${action}`;
  const descriptor = PERMISSION_LABELS[code];
  return descriptor ? i18n._(descriptor) : action.replace(/-/g, " ");
}

function getResourceMeta(resource: string, i18n: I18n): { label: string; description: string } {
  const meta = RESOURCE_LABELS[resource];
  if (meta) return { label: i18n._(meta.label), description: i18n._(meta.description) };
  return { label: resource, description: i18n._(msg`Permissions for ${resource}.`) };
}

// ── Permission evaluation ────────────────────────────────────────────────────

function userHasPermission(
  effectivePermissions: Set<string>,
  resource: string,
  action: string,
): boolean {
  return satisfiesPermission(effectivePermissions, `${resource}:${action}`);
}

// ── Sub-components ───────────────────────────────────────────────────────────

function PermissionsSkeleton({ canEdit }: { canEdit: boolean }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-36" />
          <Skeleton className="h-4 w-64" />
        </div>
        {canEdit && <Skeleton className="h-10 w-48" />}
      </div>
      <div className="h-px bg-(--shell-border)" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
          <div className="w-full shrink-0 md:w-[340px] flex flex-col gap-2">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-4 w-52" />
          </div>
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, j) => (
              <Skeleton key={j} className="h-5 w-48" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PermissionGroupRow({
  group,
  effectivePermissions,
}: {
  group: PermissionGroup;
  effectivePermissions: Set<string>;
}) {
  const { i18n } = useLingui();
  const meta = getResourceMeta(group.resource, i18n);
  return (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
        <div className="w-full shrink-0 md:w-[340px]">
          <p className="text-sm font-medium text-(--color-text-primary)">{meta.label}</p>
          <p className="mt-1 text-xs text-(--color-text-secondary)">{meta.description}</p>
        </div>
        <div className="flex flex-1 flex-col gap-3">
          {group.actions.map((action) => {
            const checked = userHasPermission(effectivePermissions, group.resource, action);
            return (
              <label
                key={action}
                className="flex cursor-default items-center gap-3 pointer-events-none"
              >
                <Checkbox
                  checked={checked}
                  disabled
                  className={cn(
                    "shrink-0",
                    checked && "data-[state=checked]:bg-(--color-action) data-[state=checked]:border-(--color-action)",
                  )}
                />
                <span
                  className={cn(
                    "text-sm font-medium",
                    checked ? "text-(--color-text-primary)" : "text-(--color-text-secondary)",
                  )}
                >
                  {getPermissionLabel(group.resource, action, i18n)}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="h-px bg-(--shell-border)" />
    </>
  );
}

function EmptyPermissionsState() {
  const { t } = useLingui();
  return (
    <div className="py-10 text-center text-sm text-(--color-text-secondary)">
      {t`No roles are assigned to this user. Assign a role to see effective permissions.`}
    </div>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────

interface UserPermissionsSectionProps {
  roleAssignments: UserRoleAssignment[];
}

export function UserPermissionsSection({
  roleAssignments,
}: UserPermissionsSectionProps) {
  const { t } = useLingui();
  const { isAuthenticated, hasRole } = useAuth();
  // /rolesperms is gated to SUPER_ADMIN only — only show the link to super admins.
  const canConfigureRoles = hasRole("SUPER_ADMIN");

  const primaryRoleId = useMemo(() => {
    const primary =
      roleAssignments.find((a) => a.role.code !== "SUPER_ADMIN") ??
      roleAssignments[0];
    return primary?.role.id;
  }, [roleAssignments]);

  const { data: permissionGroups = [], isLoading } = useQuery({
    queryKey: ["roles", "permissions"],
    queryFn: () => roleApi.getPermissions(),
    enabled: isAuthenticated,
  });

  const effectivePermissions = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const assignment of roleAssignments) {
      for (const perm of assignment.role.permissions) {
        set.add(perm);
      }
    }
    return set;
  }, [roleAssignments]);

  if (isLoading) {
    return <PermissionsSkeleton canEdit={false} />;
  }

  return (
    <div>
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-lg font-medium text-(--color-text-primary)">{t`Permissions`}</p>
            <p className="text-sm leading-5 text-(--color-text-secondary)">
              {t`What these roles let the user do. Read-only — change the roles above to change this.`}
            </p>
          </div>
          {canConfigureRoles && (
            <Link to="/rolesperms" search={{ roleId: primaryRoleId, tab: "roles" }}>
              <Button
                variant="outline"
                className="h-10 shrink-0 gap-2 rounded-[10px] border-(--shell-border) px-4"
              >
                <Settings className="size-4 text-(--color-text-primary)" />
                <span className="text-sm font-medium text-(--color-text-primary)">{t`Configure permissions`}</span>
              </Button>
            </Link>
          )}
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      <div className="mt-5 flex flex-col gap-5">
        {permissionGroups.length === 0 || roleAssignments.length === 0 ? (
          <EmptyPermissionsState />
        ) : (
          permissionGroups.map((group) => (
            <PermissionGroupRow
              key={group.resource}
              group={group}
              effectivePermissions={effectivePermissions}
            />
          ))
        )}
      </div>

    </div>
  );
}
